<script setup lang="ts">
import { ref, computed } from 'vue';
import InfoWindow from '../map/InfoWindow.vue';
import BaseSelect from '@/components/base/BaseSelect.vue';
import BaseButton from '../base/BaseButton.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import MarkerQuestions from '@/components/map/MarkerQuestions.vue';
import { getLabel } from '@/utils/data-display-utils';
import { getTranslation } from '@/utils/data-display-utils';
import { useForm, useField, ErrorMessage } from 'vee-validate';
import * as yup from 'yup';
import { Locales } from '@/store/language.interface';

// PROPS
const props = defineProps<{
  infoVisible?: boolean;
  userEditing?: boolean;
  addingData?: boolean;
  markerSelected?: boolean;
  isFullscreen?: boolean;
  mapConfig: any;
  languageEnabled?: Locales[];
}>();

// EMITS
const emit = defineEmits<{
  (e: 'setMarker'): void;
  (e: 'setUserComment', comment): void;
  (e: 'changeCategory', category): void;
  (e: 'closeMarker'): void;
  (e: 'closeInfo'): void;
  (e: 'closeEditing'): void;
  (e: 'closeAdding'): void;
  (e: 'pointAction'): void;
}>();

// FORM
const validationSchema = yup.object().shape({
  comment: yup.string().required(() => getLabel('errors.required', props.languageEnabled)),
});
const { handleSubmit, meta, errors } = useForm({
  validationSchema,
});

// COMPUTED
const categoryName = computed(() => {
  return props.mapConfig.categories.find(
    (category) => category.id === props.markerSelected[0].properties.markerCategoryId,
  );
});

const marker = computed(() => {
  return props.markerSelected[0].properties;
});

// FUNCTIONS
function setUserMarker() {
  emit('setMarker');
}

const setUserComment = handleSubmit(async (values) => {
  emit('setUserComment', values.comment);
});

function changeMarkerCategory(e) {
  emit('changeCategory', e);
}

function closeMarker() {
  emit('closeMarker');
}

function closeInfo() {
  emit('closeInfo');
}

function closeEditing() {
  emit('closeEditing');
}

function closeAdding() {
  emit('closeAdding');
}

function pointAction() {
  emit('pointAction');
}
</script>
<template>
  <div :class="['bottom-windows', { fullscreenInfo: props.isFullscreen }]">
    <InfoWindow v-if="!props.isFullscreen && props.infoVisible" @close="closeInfo">
      <template v-slot:title>{{ getLabel('map.instructions.title', props.languageEnabled) }}</template>
      <template v-slot:content>
        <div>{{ getLabel('map.instructions.content', props.languageEnabled) }}</div>
      </template>
      <template v-slot:close>
        <q-icon name="close" @click="closeInfo" />
      </template>
    </InfoWindow>
    <InfoWindow v-if="!props.isFullscreen && userEditing && !props.infoVisible" @close="closeEditing">
      <template v-slot:title>{{ getLabel('map.add-marker.title', props.languageEnabled) }}</template>
      <template v-slot:content>
        <div>{{ getLabel('map.add-marker.content', props.languageEnabled) }}</div>
        <BaseButton
          class="action set-location"
          icon="location_searching"
          :label="getLabel('map.add-marker.button', props.languageEnabled)"
          @click="setUserMarker"
        />
      </template>
      <template v-slot:close>
        <q-icon name="close" @click="closeEditing" />
      </template>
    </InfoWindow>
    <InfoWindow v-if="!props.isFullscreen && addingData && !props.infoVisible" @close="closeAdding">
      <template v-slot:title>{{ getLabel('map.selected-point.title', props.languageEnabled) }}</template>
      <template v-slot:content>
        <MarkerQuestions
          @changeCategory="changeMarkerCategory($event)"
          :mapConfig="props.mapConfig"
          :language-enabled="props.languageEnabled"
        />
        <BaseButton
          class="action"
          :label="getLabel('buttons.submit', props.languageEnabled)"
          @click="setUserComment"
          :disabled="!meta.valid"
        />
      </template>
      <template v-slot:close>
        <q-icon name="close" @click="closeAdding" />
      </template>
    </InfoWindow>
    <InfoWindow v-if="markerSelected && !props.infoVisible" @close="closeMarker">
      <template v-slot:content>
        <slot
          name="marker-content"
          :marker="marker"
          :category="getTranslation(categoryName, 'name', languageEnabled)"
          :action="pointAction"
        />
      </template>
      <template v-slot:close>
        <q-icon name="close" @click="closeMarker" />
      </template>
    </InfoWindow>
  </div>
</template>
<style lang="scss">
.bottom-windows {
  display: flex;
}
.fullscreenInfo {
  width: 50%;
  bottom: 40px;
  position: absolute;
  left: 0;
  right: 0;
  margin: auto;
}
</style>
